import { Checkbox, Col, Form, FormInstance, Input, Row } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { WarningOutlined } from '@ant-design/icons';

import { useCustomerPhone } from '../hooks';
import { useFormFieldConfirmations } from '../hooks';
import { getFormRules, toDoNotEmailEstimate } from '../utils';
import * as St from './CustomerDataForm.styles';

import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { CustomerDataModel, resolveShowSendEmailOption } from '~/core/models';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedCustomerData } from '~/redux/slices/customerData';

interface CustomerDataFormProps {
  form: FormInstance<CustomerDataModel>;
  isSendingEstimateInfo: boolean;
  onConfirmSuccess: (values: CustomerDataModel) => Promise<void>;
}

const CustomerDataForm = ({
  form,
  isSendingEstimateInfo,
  onConfirmSuccess,
}: CustomerDataFormProps) => {
  const { Item } = Form;

  const { t } = useTranslation();

  const selectedCustomerData = useAppSelector(selectSelectedCustomerData);

  const { isOnlineRetailer, retailerFileSettings } = useRetailerSettings();
  const showSendEmailOption = resolveShowSendEmailOption(
    isOnlineRetailer,
    retailerFileSettings?.showSendEmailOption
  );

  useEffect(() => {
    form.setFieldsValue({
      doNotEmailEstimate: toDoNotEmailEstimate(
        selectedCustomerData?.sendCustomerEmail
      ),
    });
  }, [form, selectedCustomerData?.sendCustomerEmail]);

  const { isPhoneRequired, onChangePhones } = useCustomerPhone(form);

  const {
    addressConfirmed,
    handleInputBlur,
    nameConfirmed,
    setAddressConfirmed,
    setNameConfirmed,
    showConfirmAddress,
    showConfirmName,
  } = useFormFieldConfirmations(form);

  const formRules = getFormRules(t);

  const nameLabel = (
    <>
      {t('SAVE.LABEL.NAME')}
      {showConfirmName ? (
        <>
          <St.RequiredCheckbox
            name="confirmName"
            valuePropName="checked"
            rules={[
              {
                required: showConfirmName,
                message: t('SAVE.MESSAGES.CONFIRM_NAME'),
              },
            ]}
          >
            <WarningOutlined />
            <input
              type="checkbox"
              name="confirmName"
              checked={nameConfirmed}
              onChange={(e) => {
                setNameConfirmed(e.target.checked);
                form.setFieldsValue({ confirmName: e.target.checked });
              }}
            />
            <label>{t('SAVE.LABEL.CONFIRM_NAME')}</label>
          </St.RequiredCheckbox>
        </>
      ) : null}
    </>
  );

  const addressLabel = (
    <>
      {t('SAVE.LABEL.ADDRESS1')}
      {showConfirmAddress ? (
        <St.RequiredCheckbox
          name="confirmAddress"
          valuePropName="addressConfirmed"
          rules={[
            {
              required: showConfirmAddress,
              message: t('SAVE.MESSAGES.CONFIRM_ADDRESS'),
            },
          ]}
        >
          <WarningOutlined />
          <input
            type="checkbox"
            name="confirmAddress"
            checked={addressConfirmed}
            onChange={(e) => {
              setAddressConfirmed(e.target.checked);
              form.setFieldsValue({ confirmAddress: e.target.checked });
            }}
          />
          <label>{t('SAVE.LABEL.CONFIRM_ADDRESS')}</label>
        </St.RequiredCheckbox>
      ) : null}
    </>
  );

  return (
    <St.Container
      name="basic"
      form={form}
      onBlur={handleInputBlur}
      disabled={isSendingEstimateInfo}
      initialValues={{
        ...selectedCustomerData,
        doNotEmailEstimate: toDoNotEmailEstimate(
          selectedCustomerData?.sendCustomerEmail
        ),
      }}
      onFinish={onConfirmSuccess}
      autoComplete="off"
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <St.RequiredField
        label={nameLabel}
        name="customerName"
        rules={formRules.customerName}
      >
        <Input />
      </St.RequiredField>
      <St.DoubleRequiredField
        label={addressLabel}
        name="address1"
        rules={formRules.address1}
      >
        <Input />
      </St.DoubleRequiredField>
      <Item
        label={t('SAVE.LABEL.ADDRESS2')}
        name="address2"
        rules={formRules.address2}
      >
        <Input />
      </Item>
      <Row gutter={16}>
        <Col span={12}>
          <St.RequiredField
            label={t('SAVE.LABEL.CITY')}
            name="city"
            rules={formRules.city}
          >
            <Input disabled />
          </St.RequiredField>
        </Col>
        <Col span={6}>
          <St.RequiredField
            label={t('SAVE.LABEL.STATE')}
            name="state"
            rules={formRules.state}
          >
            <Input disabled />
          </St.RequiredField>
        </Col>
        <Col span={6}>
          <St.RequiredField
            label={t('SAVE.LABEL.ZIP')}
            name="zip"
            rules={formRules.zip}
          >
            <Input disabled />
          </St.RequiredField>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <St.RequiredField
            label={t('SAVE.LABEL.PHONE')}
            name="phone"
            rules={
              isPhoneRequired
                ? formRules.phone.concat(formRules.phoneRequired)
                : formRules.phone
            }
            validateTrigger="onChange"
          >
            <Input
              placeholder="XXX-XXX-XXXX"
              maxLength={20}
              onChange={onChangePhones('main')}
            />
          </St.RequiredField>
        </Col>
        <Col span={12}>
          <St.RequiredField
            label={t('SAVE.LABEL.ALT_PHONE')}
            name="alternatePhone"
            rules={formRules.alternatePhone}
          >
            <Input
              placeholder="XXX-XXX-XXXX"
              maxLength={20}
              onChange={onChangePhones('alternate')}
            />
          </St.RequiredField>
        </Col>
      </Row>
      <St.RequiredField
        label={t('SAVE.LABEL.EMAIL')}
        name="email"
        rules={formRules.email}
      >
        <Input />
      </St.RequiredField>
      {showSendEmailOption && (
        <St.SendEmailField name="doNotEmailEstimate" valuePropName="checked">
          <Checkbox>{t('SAVE.LABEL.SEND_EMAIL')}</Checkbox>
        </St.SendEmailField>
      )}
    </St.Container>
  );
};

export default CustomerDataForm;
